import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import InsightsIcon from '@mui/icons-material/Insights';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { formatEuros } from '../../../shared/string';

export interface DaySummaryData {
    totalPrice: number;
    effectivePrice: number;
    paymentsPerType: Record<string, number>;
    appointmentPaymentsTotal: number;
    packPaymentsTotal: number;
}

interface Props {
    loading: boolean;
    data: DaySummaryData | null;
    branchName?: string;
    showCashRegisterLink?: boolean;
    cashRegisterPath: string;
    onShow: () => void;
    onClose: () => void;
}

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled.div`
    margin-top: var(--spacer-S);
`;

const TriggerButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border: var(--border-thin) solid var(--surface-border-subtle);
    border-radius: 14px;
    background: linear-gradient(135deg, var(--surface-brand-soft) 0%, white 100%);
    color: var(--text-strong);
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    text-align: left;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
        border-color: var(--brand-secondary);
    }

    &:disabled {
        cursor: progress;
        opacity: 0.85;
    }
`;

const TriggerIcon = styled.div`
    flex-shrink: 0;
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 12px;
    background: var(--brand-secondary);
    color: var(--brand-accent);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 1.4rem;
        height: 1.4rem;
    }
`;

const TriggerText = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TriggerTitle = styled.span`
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--text-strong);
`;

const TriggerSubtitle = styled.span`
    font-size: 0.85rem;
    color: var(--text-muted);
`;

const TriggerArrow = styled.div`
    flex-shrink: 0;
    color: var(--text-muted);
    display: flex;
    align-items: center;
`;

const Card = styled.div`
    border: var(--border-thin) solid var(--surface-border-subtle);
    border-radius: 14px;
    background: white;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    animation: ${fadeIn} 0.2s ease;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
    color: var(--brand-accent);
`;

const CardHeaderTitle = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    font-size: 1rem;
    font-weight: 700;
`;

const CloseButton = styled(IconButton).attrs({ size: 'small' })`
    && {
        color: white;
        opacity: 0.85;
        &:hover { opacity: 1; background: rgba(255,255,255,0.15); }
    }
`;

const CardBody = styled.div`
    padding: var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const TotalsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-S);

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StatBox = styled.div<{ $accent?: boolean }>`
    padding: var(--spacer-S);
    border-radius: 12px;
    border: var(--border-thin) solid var(--surface-border-subtle);
    background: ${({ $accent }) => $accent ? 'var(--surface-brand-soft)' : 'var(--bg-light)'};
`;

const StatLabel = styled.div`
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    margin-bottom: var(--spacer-XXS);
`;

const StatValue = styled.div`
    font-size: 1.45rem;
    font-weight: 700;
    color: var(--text-strong);
    line-height: 1.1;
`;

const StatHint = styled.div`
    font-size: 0.73rem;
    color: var(--text-muted);
    margin-top: var(--spacer-XXS);
    line-height: 1.3;
`;

const PaymentsBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const PaymentsTitleRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const PaymentsTitle = styled.div`
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
`;

const PaymentsHint = styled.div`
    font-size: 0.73rem;
    color: var(--text-muted);
    line-height: 1.3;
`;

const PaymentList = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    border: var(--border-thin) solid var(--surface-border-subtle);
    overflow: hidden;
`;

const PaymentRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.7rem var(--spacer-S);
    background: white;

    & + & {
        border-top: var(--border-thin) solid var(--surface-border-subtle);
    }
`;

const PaymentType = styled.span`
    font-size: 0.95rem;
    color: var(--text-body);
    text-transform: capitalize;
`;

const PaymentValue = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--text-strong);
`;

const EmptyHint = styled.div`
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: center;
    padding: var(--spacer-S) 0;
`;

const OriginRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.82rem;
    color: var(--text-muted);
    padding: var(--spacer-XXS) 0;

    & + & {
        border-top: var(--border-thin) solid var(--surface-border-subtle);
    }
`;

const OriginLabel = styled.span``;

const OriginValue = styled.span`
    font-weight: 600;
    color: var(--text-body);
`;

const CashRegisterLinkWrapper = styled.div`
    text-align: center;

    a {
        display: inline-flex;
        align-items: center;
        gap: var(--spacer-XS);
        font-size: 0.85rem;
        color: var(--appBlue);
        text-decoration: none;

        &:hover {
            text-decoration: underline;
            color: var(--appBlue);
        }
    }
`;

const DaySummary: React.FC<Props> = ({ loading, data, branchName, showCashRegisterLink, cashRegisterPath, onShow, onClose }) => {
    const t = useTranslations();

    if (!data) {
        return (
            <Section>
                <TriggerButton type="button" onClick={onShow} disabled={loading} aria-busy={loading}>
                    <TriggerIcon><InsightsIcon /></TriggerIcon>
                    <TriggerText>
                        <TriggerTitle>{t(TK.seeDaySummary)}{branchName ? ` · ${branchName}` : ''}</TriggerTitle>
                        <TriggerSubtitle>{t(TK.totalsAndPaymentsForTheDay)}</TriggerSubtitle>
                    </TriggerText>
                    <TriggerArrow>
                        {loading ? <CircularProgress size={20} thickness={5} /> : <ChevronRightIcon />}
                    </TriggerArrow>
                </TriggerButton>
            </Section>
        );
    }

    const paymentEntries = Object.entries(data.paymentsPerType);

    return (
        <Section>
            <Card>
                <CardHeader>
                    <CardHeaderTitle>
                        <InsightsIcon fontSize="small" />
                        {t(TK.daySummary)}
                    </CardHeaderTitle>
                    <CloseButton onClick={onClose} aria-label="close">
                        <CloseIcon fontSize="small" />
                    </CloseButton>
                </CardHeader>
                <CardBody>
                    <TotalsRow>
                        <StatBox $accent>
                            <StatLabel>{t(TK.total)}</StatLabel>
                            <StatValue>{formatEuros(data.totalPrice)}</StatValue>
                            <StatHint>{t(TK.daySummaryTotalHint)}</StatHint>
                        </StatBox>
                        <StatBox>
                            <StatLabel>{t(TK.effective)}</StatLabel>
                            <StatValue>{formatEuros(data.effectivePrice)}</StatValue>
                            <StatHint>{t(TK.daySummaryEffectiveHint)}</StatHint>
                        </StatBox>
                    </TotalsRow>

                    <PaymentsBlock>
                        <PaymentsTitleRow>
                            <PaymentsTitle>{t(TK.paymentsOfDay)}</PaymentsTitle>
                            <PaymentsHint>{t(TK.daySummaryPaymentsHint)}</PaymentsHint>
                        </PaymentsTitleRow>
                        {paymentEntries.length === 0
                            ? <EmptyHint>—</EmptyHint>
                            : (
                                <PaymentList>
                                    {paymentEntries.map(([type, value]) => (
                                        <PaymentRow key={type}>
                                            <PaymentType>{type}</PaymentType>
                                            <PaymentValue>{formatEuros(value)}</PaymentValue>
                                        </PaymentRow>
                                    ))}
                                </PaymentList>
                            )
                        }
                        {(data.appointmentPaymentsTotal > 0 || data.packPaymentsTotal > 0) && (
                            <div>
                                {data.appointmentPaymentsTotal > 0 && (
                                    <OriginRow>
                                        <OriginLabel>{t(TK.fromAppointments)}</OriginLabel>
                                        <OriginValue>{formatEuros(data.appointmentPaymentsTotal)}</OriginValue>
                                    </OriginRow>
                                )}
                                {data.packPaymentsTotal > 0 && (
                                    <OriginRow>
                                        <OriginLabel>{t(TK.fromPacks)}</OriginLabel>
                                        <OriginValue>{formatEuros(data.packPaymentsTotal)}</OriginValue>
                                    </OriginRow>
                                )}
                            </div>
                        )}
                    </PaymentsBlock>
                    {showCashRegisterLink && (
                        <CashRegisterLinkWrapper>
                            <a href={cashRegisterPath}>
                                {t(TK.seeDetailsInCashRegister)}
                                <ChevronRightIcon fontSize="small" />
                            </a>
                        </CashRegisterLinkWrapper>
                    )}
                </CardBody>
            </Card>
        </Section>
    );
};

export default DaySummary;
